import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  AuthFlowType,
} from '@aws-sdk/client-cognito-identity-provider';

const config = {
  region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
  userPoolId: process.env.REACT_APP_USER_POOL_ID!,
  userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID!,
};

const cognitoClient = new CognitoIdentityProviderClient({
  region: config.region,
});

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

class AuthService {
  private tokens: AuthTokens | null = null;

  constructor() {
    // Try to load tokens from localStorage on initialization
    this.loadTokensFromStorage();
  }

  private saveTokensToStorage(tokens: AuthTokens) {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    this.tokens = tokens;
  }

  private loadTokensFromStorage() {
    const stored = localStorage.getItem('auth_tokens');
    if (stored) {
      try {
        this.tokens = JSON.parse(stored);
      } catch (error) {
        console.error('Failed to parse stored tokens:', error);
        localStorage.removeItem('auth_tokens');
      }
    }
  }

  private clearTokensFromStorage() {
    localStorage.removeItem('auth_tokens');
    this.tokens = null;
  }

  async signUp(data: SignUpData) {
    const command = new SignUpCommand({
      ClientId: config.userPoolClientId,
      Username: data.email,
      Password: data.password,
      UserAttributes: [
        { Name: 'email', Value: data.email },
        { Name: 'given_name', Value: data.firstName },
        { Name: 'family_name', Value: data.lastName },
      ],
    });

    const response = await cognitoClient.send(command);
    return response;
  }

  async confirmSignUp(email: string, confirmationCode: string) {
    const command = new ConfirmSignUpCommand({
      ClientId: config.userPoolClientId,
      Username: email,
      ConfirmationCode: confirmationCode,
    });

    const response = await cognitoClient.send(command);
    return response;
  }

  async resendConfirmationCode(email: string) {
    const command = new ResendConfirmationCodeCommand({
      ClientId: config.userPoolClientId,
      Username: email,
    });

    const response = await cognitoClient.send(command);
    return response;
  }

  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const command = new InitiateAuthCommand({
      ClientId: config.userPoolClientId,
      AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
      AuthParameters: {
        USERNAME: credentials.email,
        PASSWORD: credentials.password,
      },
    });

    const response = await cognitoClient.send(command);

    if (response.ChallengeName) {
      throw new Error(`Authentication challenge required: ${response.ChallengeName}`);
    }

    if (!response.AuthenticationResult) {
      throw new Error('Authentication failed');
    }

    const tokens: AuthTokens = {
      accessToken: response.AuthenticationResult.AccessToken!,
      idToken: response.AuthenticationResult.IdToken!,
      refreshToken: response.AuthenticationResult.RefreshToken!,
    };

    this.saveTokensToStorage(tokens);
    return tokens;
  }

  logout() {
    this.clearTokensFromStorage();
  }

  isAuthenticated(): boolean {
    if (!this.tokens) {
      return false;
    }

    // Check if the ID token is expired
    try {
      const idToken = this.tokens.idToken;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (payload.exp < currentTime) {
        // Token is expired, clear it
        this.clearTokensFromStorage();
        return false;
      }
      
      return true;
    } catch (error) {
      // If we can't parse the token, consider it invalid
      this.clearTokensFromStorage();
      return false;
    }
  }

  getAccessToken(): string | null {
    return this.tokens?.accessToken || null;
  }

  getIdToken(): string | null {
    return this.tokens?.idToken || null;
  }
}

export const authService = new AuthService();